import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Spinner, Alert, Form, Modal, Table } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Button as PrimeButton } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import CarteV2Service from '../../../services/CarteV2Service';

/**
 * Composant pour gérer les batchs d'impression de cartes
 * Permet de créer un batch, rechercher des assurés, ajouter des cartes et enregistrer
 */
const PrintBatchManager = () => {
    const [batches, setBatches] = useState([]);
    const [currentBatch, setCurrentBatch] = useState(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showSearchDialog, setShowSearchDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const toast = React.useRef(null);

    // Formulaire nouveau batch
    const [batchForm, setBatchForm] = useState({
        batchName: '',
        countryCode: 'CI',
        plannedPrintDate: new Date(),
        priority: 'NORMAL',
        description: ''
    });

    // Recherche d'assurés
    const [searchCriteria, setSearchCriteria] = useState({
        numeroAssure: '',
        nom: '',
        prenom: '',
        dateNaissance: null,
        numeroPolice: '',
        status: 'ACTIVE'
    });
    const [searchResults, setSearchResults] = useState([]);
    const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([]);

    // Cartes du batch actuel
    const [batchCards, setBatchCards] = useState([]);

    const priorityOptions = [
        { label: 'Basse', value: 'LOW' },
        { label: 'Normale', value: 'NORMAL' },
        { label: 'Haute', value: 'HIGH' },
        { label: 'Urgente', value: 'URGENT' }
    ];

    const countryOptions = [
        { label: 'Côte d\'Ivoire', value: 'CI' },
        { label: 'Sénégal', value: 'SN' },
        { label: 'Burkina Faso', value: 'BF' },
        { label: 'Mali', value: 'ML' }
    ];

    useEffect(() => {
        loadBatches();
    }, []);

    const loadBatches = async () => {
        try {
            setLoading(true);
            const data = await CarteV2Service.getCardBatches();
            setBatches(data || []);
        } catch (error) {
            console.error('Erreur chargement batches:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors du chargement des batchs',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBatch = async () => {
        try {
            setLoading(true);
            const newBatch = await CarteV2Service.createCardBatch({
                ...batchForm,
                createdBy: localStorage.getItem('userEmail') || 'admin@company.com'
            });

            toast.current?.show({
                severity: 'success',
                summary: 'Succès',
                detail: `Batch "${batchForm.batchName}" créé avec succès`,
                life: 3000
            });

            setCurrentBatch(newBatch);
            setBatchCards([]);
            setShowCreateDialog(false);
            await loadBatches();

            // Reset form
            setBatchForm({
                batchName: '',
                countryCode: 'CI',
                plannedPrintDate: new Date(),
                priority: 'NORMAL',
                description: ''
            });
        } catch (error) {
            console.error('Erreur création batch:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erreur',
                detail: error.message || 'Erreur lors de la création du batch',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchBeneficiaries = async () => {
        try {
            setSearchLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

            // Construire le query string pour la recherche
            const params = new URLSearchParams();
            if (searchCriteria.numeroAssure) params.append('numeroAssure', searchCriteria.numeroAssure);
            if (searchCriteria.nom) params.append('nom', searchCriteria.nom);
            if (searchCriteria.prenom) params.append('prenom', searchCriteria.prenom);
            if (searchCriteria.numeroPolice) params.append('numeroPolice', searchCriteria.numeroPolice);

            // Utiliser l'API existante pour rechercher les clients/assurés
            const searchUrl = `${API_BASE}/Client/search?${params.toString()}`;
            const clientsResponse = await fetch(searchUrl);
            const clients = await clientsResponse.json();

            // Pour chaque client, vérifier s'il a des cartes disponibles
            const resultsWithCards = await Promise.all(
                clients.map(async (client) => {
                    try {
                        // Récupérer les cartes du bénéficiaire
                        const cardsResponse = await fetch(`${API_BASE}/Card/beneficiary/${client.id}`);
                        const cards = await cardsResponse.json();

                        // Trouver une carte disponible en stock
                        const availableCard = cards.find(c => c.status === 'IN_STOCK' || c.status === 'READY');

                        return {
                            ...client,
                            id: client.id,
                            numeroAssure: client.numeroAssure || client.code,
                            nom: client.lastName || client.nom,
                            prenom: client.firstName || client.prenom,
                            dateNaissance: client.birthDate || client.dateNaissance,
                            numeroPolice: client.policeNumber || client.numeroPolice,
                            availableCardSerial: availableCard?.serialNumber,
                            availableCardId: availableCard?.id,
                            stockAvailable: cards.filter(c => c.status === 'IN_STOCK').length
                        };
                    } catch (err) {
                        console.error(`Erreur récupération cartes pour client ${client.id}:`, err);
                        return {
                            ...client,
                            availableCardSerial: null,
                            stockAvailable: 0
                        };
                    }
                })
            );

            setSearchResults(resultsWithCards || []);

            if (resultsWithCards.length === 0) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Aucun résultat',
                    detail: 'Aucun assuré trouvé avec ces critères',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Erreur recherche:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de la recherche des assurés',
                life: 3000
            });
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAddCardsToBatch = async () => {
        if (!currentBatch) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Veuillez d\'abord créer ou sélectionner un batch',
                life: 3000
            });
            return;
        }

        if (selectedBeneficiaries.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Veuillez sélectionner au moins un assuré',
                life: 3000
            });
            return;
        }

        try {
            setLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
            const addedCards = [];

            for (const beneficiary of selectedBeneficiaries) {
                try {
                    // Utiliser l'API existante pour ajouter une carte au batch
                    // POST /Card/batches/{batchId}/cards
                    const response = await fetch(`${API_BASE}/Card/batches/${currentBatch.id}/cards`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            cardId: beneficiary.availableCardId,
                            beneficiaryId: beneficiary.id,
                            cardNumber: beneficiary.availableCardSerial,
                            addedBy: localStorage.getItem('userEmail') || 'admin@company.com'
                        })
                    });

                    if (response.ok) {
                        const card = await response.json();
                        addedCards.push({
                            ...card,
                            serialNumber: beneficiary.availableCardSerial,
                            beneficiaryName: `${beneficiary.prenom} ${beneficiary.nom}`,
                            numeroAssure: beneficiary.numeroAssure
                        });
                    }
                } catch (err) {
                    console.error(`Erreur pour assuré ${beneficiary.numeroAssure}:`, err);
                }
            }

            setBatchCards(prev => [...prev, ...addedCards]);
            setSelectedBeneficiaries([]);
            setShowSearchDialog(false);

            toast.current?.show({
                severity: 'success',
                summary: 'Succès',
                detail: `${addedCards.length} carte(s) ajoutée(s) au batch`,
                life: 3000
            });
        } catch (error) {
            console.error('Erreur ajout cartes:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de l\'ajout des cartes au batch',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveCardFromBatch = (cardId) => {
        setBatchCards(prev => prev.filter(c => c.id !== cardId));
        toast.current?.show({
            severity: 'info',
            summary: 'Carte retirée',
            detail: 'La carte a été retirée du batch',
            life: 2000
        });
    };

    const handleSaveBatch = async () => {
        if (!currentBatch) return;

        try {
            setLoading(true);

            // Enregistrer toutes les cartes du batch
            await CarteV2Service.updateCardBatch(currentBatch.id, {
                status: 'READY_FOR_PRINT',
                totalCards: batchCards.length,
                updatedBy: localStorage.getItem('userEmail') || 'admin@company.com'
            });

            toast.current?.show({
                severity: 'success',
                summary: 'Succès',
                detail: `Batch enregistré avec ${batchCards.length} carte(s)`,
                life: 3000
            });

            await loadBatches();
        } catch (error) {
            console.error('Erreur enregistrement:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors de l\'enregistrement du batch',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectBatch = async (batch) => {
        setCurrentBatch(batch);
        try {
            const details = await CarteV2Service.getCardBatchDetails(batch.id);
            setBatchCards(details.cards || []);
        } catch (error) {
            console.error('Erreur chargement batch:', error);
        }
    };

    // Templates
    const leftToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                <PrimeButton
                    label="Nouveau Batch"
                    icon="pi pi-plus"
                    className="p-button-success"
                    onClick={() => setShowCreateDialog(true)}
                />
                {currentBatch && (
                    <>
                        <PrimeButton
                            label="Ajouter des Cartes"
                            icon="pi pi-user-plus"
                            onClick={() => setShowSearchDialog(true)}
                        />
                        <PrimeButton
                            label="Enregistrer"
                            icon="pi pi-save"
                            className="p-button-primary"
                            onClick={handleSaveBatch}
                            disabled={batchCards.length === 0}
                        />
                    </>
                )}
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <PrimeButton
                label="Actualiser"
                icon="pi pi-refresh"
                className="p-button-help"
                onClick={loadBatches}
            />
        );
    };

    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            'DRAFT': { label: 'Brouillon', severity: 'info' },
            'READY_FOR_PRINT': { label: 'Prêt à imprimer', severity: 'success' },
            'PRINTING': { label: 'En impression', severity: 'warning' },
            'COMPLETED': { label: 'Terminé', severity: 'success' },
            'CANCELLED': { label: 'Annulé', severity: 'danger' }
        };
        const status = statusMap[rowData.status] || { label: rowData.status, severity: 'info' };
        return <Tag value={status.label} severity={status.severity} />;
    };

    const priorityBodyTemplate = (rowData) => {
        const priorityMap = {
            'LOW': { label: 'Basse', severity: 'secondary' },
            'NORMAL': { label: 'Normale', severity: 'info' },
            'HIGH': { label: 'Haute', severity: 'warning' },
            'URGENT': { label: 'Urgente', severity: 'danger' }
        };
        const priority = priorityMap[rowData.priority] || { label: rowData.priority, severity: 'info' };
        return <Tag value={priority.label} severity={priority.severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <PrimeButton
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => handleSelectBatch(rowData)}
                    tooltip="Sélectionner ce batch"
                />
            </div>
        );
    };

    return (
        <div className="print-batch-manager">
            <Toast ref={toast} />

            <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {/* Batch actuel */}
            {currentBatch && (
                <Panel header={`Batch Actuel: ${currentBatch.batchName}`} className="mb-4">
                    <Row>
                        <Col md={3}>
                            <strong>Pays:</strong> {currentBatch.countryCode}
                        </Col>
                        <Col md={3}>
                            <strong>Priorité:</strong> {priorityBodyTemplate(currentBatch)}
                        </Col>
                        <Col md={3}>
                            <strong>Statut:</strong> {statusBodyTemplate(currentBatch)}
                        </Col>
                        <Col md={3}>
                            <strong>Cartes:</strong> {batchCards.length}
                        </Col>
                    </Row>
                    {currentBatch.description && (
                        <Row className="mt-2">
                            <Col>
                                <strong>Description:</strong> {currentBatch.description}
                            </Col>
                        </Row>
                    )}

                    {/* Cartes du batch actuel */}
                    {batchCards.length > 0 && (
                        <div className="mt-3">
                            <h6>Cartes dans le batch ({batchCards.length})</h6>
                            <DataTable
                                value={batchCards}
                                paginator
                                rows={10}
                                className="mt-2"
                                emptyMessage="Aucune carte dans ce batch"
                            >
                                <Column field="cardNumber" header="Numéro Carte" sortable />
                                <Column
                                    field="serialNumber"
                                    header="N° Série"
                                    body={(rowData) => (
                                        <Tag value={rowData.serialNumber || 'N/A'} severity="info" />
                                    )}
                                    sortable
                                />
                                <Column field="beneficiaryName" header="Assuré" sortable />
                                <Column field="numeroAssure" header="N° Assuré" sortable />
                                <Column
                                    field="stockLocation"
                                    header="Emplacement Stock"
                                    sortable
                                />
                                <Column
                                    header="Action"
                                    body={(rowData) => (
                                        <PrimeButton
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-text p-button-danger"
                                            onClick={() => handleRemoveCardFromBatch(rowData.id)}
                                            tooltip="Retirer du batch"
                                        />
                                    )}
                                />
                            </DataTable>
                        </div>
                    )}
                </Panel>
            )}

            {/* Liste des batchs */}
            <Panel header="Liste des Batchs d'Impression">
                <DataTable
                    value={batches}
                    loading={loading}
                    paginator
                    rows={10}
                    emptyMessage="Aucun batch trouvé"
                    className="mt-2"
                >
                    <Column field="batchName" header="Nom du Batch" sortable />
                    <Column field="countryCode" header="Pays" sortable />
                    <Column field="totalCards" header="Cartes" sortable />
                    <Column field="priority" header="Priorité" body={priorityBodyTemplate} sortable />
                    <Column field="status" header="Statut" body={statusBodyTemplate} sortable />
                    <Column
                        field="plannedPrintDate"
                        header="Date Prévue"
                        body={(rowData) => new Date(rowData.plannedPrintDate).toLocaleDateString('fr-FR')}
                        sortable
                    />
                    <Column header="Actions" body={actionBodyTemplate} />
                </DataTable>
            </Panel>

            {/* Dialog Création Batch */}
            <Dialog
                visible={showCreateDialog}
                style={{ width: '600px' }}
                header="Créer un Nouveau Batch d'Impression"
                modal
                onHide={() => setShowCreateDialog(false)}
            >
                <div className="p-fluid">
                    <div className="field mb-3">
                        <label htmlFor="batchName">Nom du Batch *</label>
                        <InputText
                            id="batchName"
                            value={batchForm.batchName}
                            onChange={(e) => setBatchForm({ ...batchForm, batchName: e.target.value })}
                            placeholder="Ex: Batch Impression Mars 2024"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="countryCode">Pays *</label>
                        <Dropdown
                            id="countryCode"
                            value={batchForm.countryCode}
                            options={countryOptions}
                            onChange={(e) => setBatchForm({ ...batchForm, countryCode: e.value })}
                            placeholder="Sélectionner un pays"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="priority">Priorité</label>
                        <Dropdown
                            id="priority"
                            value={batchForm.priority}
                            options={priorityOptions}
                            onChange={(e) => setBatchForm({ ...batchForm, priority: e.value })}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="plannedPrintDate">Date d'Impression Prévue</label>
                        <Calendar
                            id="plannedPrintDate"
                            value={batchForm.plannedPrintDate}
                            onChange={(e) => setBatchForm({ ...batchForm, plannedPrintDate: e.value })}
                            dateFormat="dd/mm/yy"
                            showIcon
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="description">Description</label>
                        <InputText
                            id="description"
                            value={batchForm.description}
                            onChange={(e) => setBatchForm({ ...batchForm, description: e.target.value })}
                            placeholder="Description optionnelle"
                        />
                    </div>

                    <div className="flex justify-content-end gap-2 mt-4">
                        <PrimeButton
                            label="Annuler"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setShowCreateDialog(false)}
                        />
                        <PrimeButton
                            label="Créer"
                            icon="pi pi-check"
                            onClick={handleCreateBatch}
                            disabled={!batchForm.batchName || loading}
                            loading={loading}
                        />
                    </div>
                </div>
            </Dialog>

            {/* Dialog Recherche Assurés */}
            <Dialog
                visible={showSearchDialog}
                style={{ width: '900px' }}
                header="Rechercher et Ajouter des Assurés"
                modal
                onHide={() => setShowSearchDialog(false)}
            >
                <div className="p-fluid">
                    {/* Critères de recherche */}
                    <Panel header="Critères de Recherche" className="mb-3">
                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="numeroAssure">Numéro Assuré</label>
                                    <InputText
                                        id="numeroAssure"
                                        value={searchCriteria.numeroAssure}
                                        onChange={(e) => setSearchCriteria({ ...searchCriteria, numeroAssure: e.target.value })}
                                        placeholder="Ex: 123456789"
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="nom">Nom</label>
                                    <InputText
                                        id="nom"
                                        value={searchCriteria.nom}
                                        onChange={(e) => setSearchCriteria({ ...searchCriteria, nom: e.target.value })}
                                        placeholder="Nom de famille"
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="prenom">Prénom</label>
                                    <InputText
                                        id="prenom"
                                        value={searchCriteria.prenom}
                                        onChange={(e) => setSearchCriteria({ ...searchCriteria, prenom: e.target.value })}
                                        placeholder="Prénom"
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field mb-3">
                                    <label htmlFor="numeroPolice">N° Police</label>
                                    <InputText
                                        id="numeroPolice"
                                        value={searchCriteria.numeroPolice}
                                        onChange={(e) => setSearchCriteria({ ...searchCriteria, numeroPolice: e.target.value })}
                                        placeholder="Numéro de police"
                                    />
                                </div>
                            </Col>
                        </Row>
                        <div className="flex justify-content-end">
                            <PrimeButton
                                label="Rechercher"
                                icon="pi pi-search"
                                onClick={handleSearchBeneficiaries}
                                loading={searchLoading}
                            />
                        </div>
                    </Panel>

                    {/* Résultats */}
                    {searchResults.length > 0 && (
                        <Panel header={`Résultats (${searchResults.length})`}>
                            <DataTable
                                value={searchResults}
                                selection={selectedBeneficiaries}
                                onSelectionChange={(e) => setSelectedBeneficiaries(e.value)}
                                paginator
                                rows={5}
                            >
                                <Column selectionMode="multiple" style={{ width: '3em' }} />
                                <Column field="numeroAssure" header="N° Assuré" sortable />
                                <Column field="nom" header="Nom" sortable />
                                <Column field="prenom" header="Prénom" sortable />
                                <Column field="dateNaissance" header="Date Naissance" body={(rowData) => new Date(rowData.dateNaissance).toLocaleDateString('fr-FR')} />
                                <Column field="numeroPolice" header="N° Police" sortable />
                                <Column
                                    field="availableCardSerial"
                                    header="N° Série Dispo"
                                    body={(rowData) => (
                                        rowData.availableCardSerial ? (
                                            <Tag value={rowData.availableCardSerial} severity="success" icon="pi pi-check" />
                                        ) : (
                                            <Tag value="Aucune" severity="warning" icon="pi pi-exclamation-triangle" />
                                        )
                                    )}
                                    sortable
                                />
                                <Column
                                    field="stockAvailable"
                                    header="Stock"
                                    body={(rowData) => (
                                        <Badge bg={rowData.stockAvailable > 0 ? 'success' : 'danger'}>
                                            {rowData.stockAvailable || 0}
                                        </Badge>
                                    )}
                                />
                            </DataTable>
                        </Panel>
                    )}

                    <div className="flex justify-content-between mt-4">
                        <div className="text-muted">
                            {selectedBeneficiaries.length > 0 && `${selectedBeneficiaries.length} assuré(s) sélectionné(s)`}
                        </div>
                        <div className="flex gap-2">
                            <PrimeButton
                                label="Annuler"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setShowSearchDialog(false)}
                            />
                            <PrimeButton
                                label="Ajouter au Batch"
                                icon="pi pi-check"
                                onClick={handleAddCardsToBatch}
                                disabled={selectedBeneficiaries.length === 0 || loading}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>

            <style>{`
                .print-batch-manager .p-toolbar {
                    background: white;
                    border: 1px solid #dee2e6;
                }

                .print-batch-manager .p-panel {
                    background: white;
                }

                .print-batch-manager .field label {
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    display: block;
                }
            `}</style>
        </div>
    );
};

export default PrintBatchManager;
